import { prop } from '../utils/immutableHelper';
import { getTimestamp } from '../utils/timeUtil';

/**
 * A single per-user reaction row. Matches `MNReactionContent` on elements and the task item
 * `reactions` node attr, so the same toggle logic can be reused across both.
 */
export type ReactionRow = {
    userId: string;
    emojiCode: string;
    timestamp: number;
};

// `prop` reads the field from either a plain object or an Immutable structure, so this works
// for both element reaction rows and plain node-attr reaction rows.
const getReactionUserId = (reaction: unknown): string => prop('userId', reaction) as string;
const getReactionEmojiCode = (reaction: unknown): string => prop('emojiCode', reaction) as string;

const reactionMatches =
    (userId: string, emojiCode: string) =>
    (reaction: ReactionRow): boolean =>
        getReactionUserId(reaction) === userId && getReactionEmojiCode(reaction) === emojiCode;

/**
 * Toggles `emojiCode` for `userId` within `existingReactionState`: removes the matching row when it
 * already exists, otherwise appends a new one. Non-mutating and shared by element, comment and task item
 * reactions so their toggle behaviour stays consistent.
 */
export const createReactionState = ({
    existingReactionState = [],
    emojiCode,
    userId,
}: {
    existingReactionState?: ReactionRow[];
    emojiCode: string;
    userId: string;
}): ReactionRow[] => {
    const matchingReaction = existingReactionState.find(reactionMatches(userId, emojiCode));

    if (matchingReaction) {
        return existingReactionState.filter((reaction) => !reactionMatches(userId, emojiCode)(reaction));
    }

    return [...existingReactionState, { userId, emojiCode, timestamp: getTimestamp() }];
};
